import { AssistantHelpCategory, AssistantMessage } from './ignite-assistant.model';

/**
 * Greeting shown when a fresh conversation starts.
 */
export const ASSISTANT_GREETING: AssistantMessage = {
  role: 'bot',
  text: 'Hi. I can help you find and explore your audiences, campaigns, campaign reports, SLA reports, and consumer profiles. What would you like to look for? Tap the help icon above for more example questions.',
};

/**
 * Starter prompts offered before the first question. The assistant is a
 * read-only audience and campaign agent, so these cover both.
 */
export const ASSISTANT_PRESET_QUESTIONS: readonly string[] = ['Show me cardiology audiences', 'Which audiences reach diabetes patients?', 'What are the top-line SLA numbers for cardiology this year?', "What's the status of my cardiology campaign?"];

/** Shown as a bot message when a request to the assistant API fails. */
export const ASSISTANT_ERROR_MESSAGE = 'Sorry — I couldn’t reach the assistant just now. Please try again in a moment.';

/** Conversations fetched per "page" of the history list — enough to fill the flyout without an immediate "Load more". */
export const ASSISTANT_HISTORY_PAGE_SIZE = 20;

/** Placeholder rows shown while the history list is (re)loading — enough to fill the flyout's visible height. */
export const ASSISTANT_HISTORY_SKELETON_ROWS: readonly number[] = [0, 1, 2];

/** History-row titles longer than this are truncated with an ellipsis, matching the previous localStorage-based history titles. */
export const ASSISTANT_HISTORY_TITLE_MAX_LENGTH = 40;

/** Maximum characters allowed in a single user prompt (keeps questions concise and blocks large paste dumps). */
export const ASSISTANT_MAX_INPUT_LENGTH = 500;

/** Placeholder shown in the composer when the assistant is idle and ready for a question. */
export const ASSISTANT_INPUT_PLACEHOLDER = 'Ask a question…';

/** Placeholder shown in the composer while a reply is in flight and the input is disabled. */
export const ASSISTANT_WAITING_PLACEHOLDER = 'Please wait while I prepare a response…';

/** Example questions shown in the help view, grouped by capability. */
export const ASSISTANT_HELP_CATEGORIES: readonly AssistantHelpCategory[] = [
  {
    category: 'Audiences',
    description: 'Find and inspect audience segments',
    questions: [
      'Any audiences with "diabetes" in the name?',
      'Which audiences are tied to the Q3 Cardiology Outreach campaign?',
      'Any audiences focused on cardiology?',
      'Can you pull up the details for the Diabetes Care Outreach audience?',
      'What criteria does the Diabetes Care Outreach audience use?',
      'Which campaign is the Diabetes Care Outreach audience linked to?',
    ],
  },
  {
    category: 'Campaigns',
    description: 'Browse and drill into campaign details',
    questions: [
      'Any campaigns about "flu season"?',
      'What campaigns did jane@example.com create?',
      'What campaigns do we have in the cardiology service category?',
      'Which campaigns started after January 1, 2025?',
      'Which campaigns wrap up before June 30, 2025?',
      'Sort my campaigns by newest first.',
      'How many campaigns fall into each service category?',
      'Give me the details on the Q3 Cardiology Outreach campaign.',
      "What's the budget and schedule for the Q3 Cardiology Outreach campaign?",
      'Which tactics are part of the Q3 Cardiology Outreach campaign?',
    ],
  },
  {
    category: 'SLA Reports',
    description: 'Service line volumes, demographics, and trends',
    questions: [
      'What are the top-line SLA numbers for cardiology this year?',
      'How did SLA KPIs look for a market area between January and June 2025?',
      'Can you break down cardiology patients by age?',
      'Break down orthopedics patients by gender for a specific market area.',
      "What's the patient payor mix for a given market area?",
      'Which zip codes have the most oncology patients?',
      'How does audience income compare between cardiology and neurology?',
      'How does our cardiology audience engage on social media?',
      'What are the key metrics for a market area?',
      "What's the year-over-year visit change by zip code for cardiology?",
      'Can you break down patient volume by risk category?',
      'What are the total charges by encounter type for cardiology in 2025?',
      "What's the monthly trend for expected contribution margin in a specific market area?",
      'Can you break down actual reimbursement by facility?',
      'What are the top diagnoses for cardiology patients?',
      'What are the top procedures in a given market area?',
    ],
  },
  {
    category: 'Consumer Profiles',
    description: 'Demographics and engagement by market',
    questions: [
      'Give me the Consumer Profile summary for a market area.',
      "What's the age distribution of consumers in a given market area?",
      "What's the gender breakdown for consumers in a specific market area?",
      'Can you break down consumers by education level?',
      'Can you break down consumers by payor category for a specific market area?',
      "What's the household income distribution for a given market area?",
      "What's the household net worth for our consumers?",
      'How complete is our age data for consumers in a market area?',
      "What's the payor-category fill rate?",
      'How do consumers in a market area prefer to engage digitally?',
      'Which zip codes have the most consumers?',
      'How many patients do we have per service line?',
      "What's the average dollars per patient by service line?",
      'What are the visit counts by encounter type for a market area?',
      "What's the recency/frequency profile of our consumers?",
    ],
  },
  {
    category: 'Cross-Report Insights',
    description: 'Combine filters across audiences and reports',
    questions: ['Can you show cardiology patient age, but exclude a specific market area?', "What's the consumer age distribution in a market area for commercial payor only?", 'Can you show me the SLA KPIs for cardiology in Q4 2024?'],
  },
];
